// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { FormCurrencyTextField } from '@/components/form-currency-text-field';
import { PopoverForm } from '@/components/popover-form';
import { CHARGE_SCHEDULE } from '@/constants/charge-schedule';
import { InfoOutlined } from '@/constants/hardfin-standard-icons';
import { IsoCurrencyCode } from '@/currency/constants/iso';
import { formatCurrencyValue } from '@/currency/utilities/format';
import { useDisclosure } from '@/hooks/use-disclosure';
import {
  BILLING_PLAN_INPUT_WIDTH,
  BillingPlanFormValues,
  CURRENCY_INPUT_SX,
  FREQUENCY_OPTIONS,
  TOGGLE_BUTTON_GROUP_SX,
  TOGGLE_BUTTON_SX,
} from '@/project/components/billing-plan-constants';
import { MilestonePhasesEditor } from '@/project/components/milestone-phases-editor';
import {
  AmountRange,
  formatAmountRange,
} from '@/project/utilities/charge-amounts';
import { pluralize } from '@/utilities/pluralize';
import {
  Box,
  FormControl,
  Link,
  MenuItem,
  Paper,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { Decimal } from 'decimal.js';
import { RefObject, useRef, useState } from 'react';
import { Control, Controller, UseFormSetValue } from 'react-hook-form';

interface Props {
  control: Control<BillingPlanFormValues>;
  setValue: UseFormSetValue<BillingPlanFormValues>;
  hasDates: boolean;
  chargeSchedule: BillingPlanFormValues['chargeSchedule'];
  chargeFrequency: string | null;
  chargeAmountRange: AmountRange;
  numCharges: number;
  lineItemTotal: Decimal;
  computedTotal: Decimal;
  totalOverrideAmount: Decimal | null;
  totalOverride: BillingPlanFormValues['totalOverride'];
  currencyCode: IsoCurrencyCode;
  // Tooltip for the total-override currency selector, which is always locked to
  // the line item's currency.
  currencyLockedTooltip: string;
  milestonePhases: BillingPlanFormValues['milestonePhases'];
  /**
   * When true, the schedule-type toggle is locked — the plan has at least
   * one INVOICED charge and switching modes would orphan invoice history.
   * The server enforces the same rule defensively. BILLABLE is reversible
   * via mark-not-billable so it does not lock the toggle.
   */
  isScheduleTypeLocked: boolean;
  /**
   * Inline helper rendered beneath the line item total when the planned
   * total (or a localCharge) exceeds the dollar cap. Save is blocked
   * upstream while this is non-null.
   */
  boundError: string | null;
  /** Forwarded to the bound-error Typography so the drawer can scroll
   * the helper into view when a blocked save is attempted. */
  boundErrorRef: RefObject<HTMLSpanElement>;
}

export function BillingPlanChargeScheduleSection(props: Props) {
  const overridePopover = useDisclosure();
  const overrideAnchorRef = useRef<HTMLSpanElement | null>(null);
  const [overrideValueBeforeOpen, setOverrideValueBeforeOpen] =
    useState<string>('');
  const overrideDifference =
    props.totalOverrideAmount !== null
      ? props.totalOverrideAmount.minus(props.computedTotal)
      : null;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
        Charge schedule
      </Typography>
      {!props.hasDates ? (
        <Typography variant="caption" color="text.secondary">
          Please configure a start and end date on the line item to configure a
          charge schedule
        </Typography>
      ) : (
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <Controller
                control={props.control}
                name="chargeSchedule"
                render={(renderProps) => {
                  const toggleGroup = (
                    <ToggleButtonGroup
                      exclusive
                      size="small"
                      sx={TOGGLE_BUTTON_GROUP_SX}
                      value={renderProps.field.value}
                      onChange={(_event, value) => {
                        if (value !== null) {
                          renderProps.field.onChange(value);
                        }
                      }}
                      disabled={props.isScheduleTypeLocked}
                    >
                      <ToggleButton
                        value={CHARGE_SCHEDULE.PERIODIC}
                        sx={TOGGLE_BUTTON_SX}
                        disabled={props.isScheduleTypeLocked}
                      >
                        Periodic
                      </ToggleButton>
                      <ToggleButton
                        value={CHARGE_SCHEDULE.MILESTONE}
                        sx={TOGGLE_BUTTON_SX}
                        disabled={props.isScheduleTypeLocked}
                      >
                        Milestone
                      </ToggleButton>
                    </ToggleButtonGroup>
                  );
                  if (!props.isScheduleTypeLocked) return toggleGroup;
                  return (
                    <Tooltip
                      title="Schedule type cannot be changed while charges are invoiced"
                      arrow
                    >
                      <span>{toggleGroup}</span>
                    </Tooltip>
                  );
                }}
              />
              <Tooltip
                arrow
                title={
                  <Box>
                    <Typography variant="caption" fontWeight={600}>
                      Periodic:
                    </Typography>
                    <Typography variant="caption">
                      {' '}
                      charges recur at a regular frequency (daily, weekly,
                      monthly, etc.)
                    </Typography>
                    <Box sx={{ mt: 1 }} />
                    <Typography variant="caption" fontWeight={600}>
                      Milestone:
                    </Typography>
                    <Typography variant="caption">
                      {' '}
                      charges occur at specific project milestones or
                      deliverables.
                    </Typography>
                  </Box>
                }
              >
                <InfoOutlined
                  sx={{
                    fontSize: '1rem',
                    color: 'text.secondary',
                    cursor: 'help',
                  }}
                />
              </Tooltip>
            </Box>
            {props.chargeSchedule === CHARGE_SCHEDULE.PERIODIC && (
              <Controller
                control={props.control}
                name="chargeFrequency"
                render={(renderProps) => (
                  <FormControl
                    size="small"
                    sx={{
                      ...CURRENCY_INPUT_SX,
                      width: BILLING_PLAN_INPUT_WIDTH,
                    }}
                  >
                    <Select
                      displayEmpty
                      value={renderProps.field.value ?? ''}
                      onChange={(event) => {
                        renderProps.field.onChange(event.target.value || null);
                      }}
                    >
                      {FREQUENCY_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            )}
          </Box>
          {props.chargeSchedule === CHARGE_SCHEDULE.MILESTONE && (
            <MilestonePhasesEditor
              control={props.control}
              setValue={props.setValue}
              phases={props.milestonePhases}
            />
          )}
          {props.chargeSchedule === CHARGE_SCHEDULE.PERIODIC && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr auto',
                columnGap: 1,
                rowGap: 0.25,
                mt: 1,
                pl: 1,
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ alignSelf: 'center' }}
              >
                {formatAmountRange(
                  props.chargeAmountRange,
                  props.currencyCode
                ) + '/charge'}
                {' \u00D7 '}
                {props.chargeFrequency && props.numCharges > 0
                  ? pluralize(props.numCharges, { singular: 'charge' })
                  : 'Not set'}
              </Typography>
              <Typography
                ref={overrideAnchorRef}
                variant="caption"
                display="block"
                sx={{ justifySelf: 'end', alignSelf: 'center', pr: 1 }}
              >
                Line item total{' '}
                <Box component="span" fontWeight={700}>
                  {formatCurrencyValue({
                    amount: props.lineItemTotal,
                    isoCode: props.currencyCode,
                  })}
                </Box>
              </Typography>
              <Box sx={{ gridColumn: '2', justifySelf: 'end', pr: 1 }}>
                {overrideDifference !== null ? (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      alignItems: 'baseline',
                      gap: 0.5,
                    }}
                  >
                    <Typography
                      variant="caption"
                      color={
                        overrideDifference.isNegative()
                          ? 'error.main'
                          : 'success.main'
                      }
                    >
                      Override: {overrideDifference.isNegative() ? '' : '+'}
                      {formatCurrencyValue({
                        amount: overrideDifference,
                        isoCode: props.currencyCode,
                      })}
                    </Typography>
                    <Link
                      component="button"
                      type="button"
                      variant="caption"
                      onClick={() => {
                        props.setValue('totalOverride.amount', '');
                      }}
                      sx={{ cursor: 'pointer' }}
                    >
                      Clear
                    </Link>
                  </Box>
                ) : (
                  <Link
                    component="button"
                    type="button"
                    variant="caption"
                    onClick={() => {
                      setOverrideValueBeforeOpen(props.totalOverride.amount);
                      if (
                        props.totalOverride.amount.trim() === '' &&
                        props.computedTotal.greaterThan(0)
                      ) {
                        props.setValue(
                          'totalOverride.amount',
                          props.computedTotal.toFixed(
                            Math.max(2, props.computedTotal.decimalPlaces())
                          )
                        );
                      }
                      overridePopover.open();
                    }}
                    sx={{ cursor: 'pointer' }}
                  >
                    Override
                  </Link>
                )}
                <PopoverForm
                  position="right"
                  submitLabel="Override"
                  anchorElement={overrideAnchorRef.current}
                  isOpen={overridePopover.isOpen}
                  onCancel={() => {
                    props.setValue(
                      'totalOverride.amount',
                      overrideValueBeforeOpen
                    );
                    overridePopover.close();
                  }}
                  onSubmit={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    overridePopover.close();
                  }}
                >
                  <FormCurrencyTextField
                    control={props.control}
                    name="totalOverride"
                    lockTo={props.currencyCode}
                    disabledTooltip={props.currencyLockedTooltip}
                    placeholder="0.00"
                  />
                </PopoverForm>
              </Box>
            </Box>
          )}
          {props.boundError !== null && (
            <Typography
              ref={props.boundErrorRef}
              variant="caption"
              color="error.main"
              role="alert"
              sx={{ display: 'block', textAlign: 'right', mt: 0.5, pr: 1 }}
            >
              {props.boundError}
            </Typography>
          )}
        </>
      )}
    </Paper>
  );
}
